// ============================================================================
// FSA ENGAGEMENT SETTINGS SERVICE
// ============================================================================

import { get, post, put } from '../../client/axiosClient';
import { fsaEngagementEndpoints } from '../../endpoints/fsa/fsaEngagement.endpoints';
import { buildEndpointWithQuery } from '../../endpoints/base.endpoints';
import type { ApiResponse, PaginatedResponse, ID } from '../../types';
import type {
  FsaEngagementSettings,
  FsaEngagementListSearchRequest,
  CreateFsaEngagementSettingsRequest,
  UpdateFsaEngagementSettingsRequest,
  FsaEligibleEngagement,
  FsaEligibleEngagementsSearchRequest,
} from '../../types/fsa/engagementSettings.types';

class FsaEngagementService {
  /**
   * Paged list of engagements that have FSA configured. Powers the
   * "FSA Engagements" sidebar screen. Transforms backend PagedResult
   * envelope to frontend PaginatedResponse shape.
   */
  async getAll(
    params?: FsaEngagementListSearchRequest
  ): Promise<PaginatedResponse<FsaEngagementSettings>> {
    const endpoint = params
      ? buildEndpointWithQuery(
          fsaEngagementEndpoints.list(),
          params as Record<string, any>
        )
      : fsaEngagementEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Paged list of engagements that do NOT yet have FSA configured —
   * candidates for the "Configure FSA" picker in
   * `FsaEngagementSettingsModal` (create mode). Same envelope transform
   * as `getAll` so the picker can use a standard PaginatedResponse shape.
   */
  async listEligible(
    params?: FsaEligibleEngagementsSearchRequest
  ): Promise<PaginatedResponse<FsaEligibleEngagement>> {
    const endpoint = params
      ? buildEndpointWithQuery(
          fsaEngagementEndpoints.eligible(),
          params as Record<string, any>
        )
      : fsaEngagementEndpoints.eligible();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Fetch FSA settings for an engagement. Returns null in `.data` when
   * not yet configured (backend responds 200 with data: null to drive
   * the UI's "Configure FSA" CTA).
   */
  async getSettings(engagementId: ID): Promise<ApiResponse<FsaEngagementSettings | null>> {
    const response = await get<ApiResponse<FsaEngagementSettings | null>>(
      fsaEngagementEndpoints.getSettings(engagementId)
    );
    return response.data;
  }

  async createSettings(
    engagementId: ID,
    request: CreateFsaEngagementSettingsRequest
  ): Promise<ApiResponse<FsaEngagementSettings>> {
    const response = await post<ApiResponse<FsaEngagementSettings>>(
      fsaEngagementEndpoints.createSettings(engagementId),
      request
    );
    return response.data;
  }

  async updateSettings(
    engagementId: ID,
    id: ID,
    request: UpdateFsaEngagementSettingsRequest
  ): Promise<ApiResponse<FsaEngagementSettings>> {
    const response = await put<ApiResponse<FsaEngagementSettings>>(
      fsaEngagementEndpoints.updateSettings(engagementId, id),
      request
    );
    return response.data;
  }

  // ----- FSA-2.3 Roll Forward -----

  async getRollForwardSpec(priorEngagementId: ID) {
    const response = await get<ApiResponse<import('../../types/fsa/rollForward.types').FsEngagementCreationSpec>>(
      fsaEngagementEndpoints.getRollForwardSpec(priorEngagementId)
    );
    return response.data;
  }

  async checkDuplicate(priorEngagementId: ID) {
    const response = await get<ApiResponse<import('../../types/fsa/rollForward.types').DuplicateEngagementCheck | null>>(
      fsaEngagementEndpoints.duplicateCheck(priorEngagementId)
    );
    return response.data;
  }

  async createEngagement(spec: import('../../types/fsa/rollForward.types').FsEngagementCreationSpec) {
    const response = await post<ApiResponse<import('../../types/fsa/rollForward.types').FsEngagementCreationResult>>(
      fsaEngagementEndpoints.create(),
      spec
    );
    return response.data;
  }

  async getOpeningBalanceDiscrepancies(engagementId: ID) {
    const response = await get<ApiResponse<import('../../types/fsa/rollForward.types').OpeningBalanceDiscrepancy[]>>(
      fsaEngagementEndpoints.getOpeningBalanceDiscrepancies(engagementId)
    );
    return response.data;
  }
}

export const fsaEngagementService = new FsaEngagementService();
export default fsaEngagementService;
